import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Collapse, Form, Row, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { nanoid } from 'nanoid'

import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../common/copy'
import { IntegrationServiceId, TASK_EXPORT_SUPPORTED_INTEGRATIONS } from '../../common/integrations'
import { useEffectiveConfig } from '../../common/useEffectiveConfig'
import { useOutsideClick } from '../../common/useOutsideClick'

import { ReadOnlyLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { ResultOption } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/DefaultDisplay/ResultOptions/ResultOptionsButton'
import { LEXICAL_NODES } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { parseMetadata } from '../../apps/professional-app/fa/MeetingSummary/utils'
import { UserTag } from '../../apps/professional-app/fa/UserTag'

import { CreateTaskPayload } from '../../store/meetingSupport/meetingSupport'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectTasks } from '../../store/task/task'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetMeQuery, useGetUsersQuery } from '../../store/user/user'
import { showToast } from '../../store/meetingSummary/display'

import { Task, TaskStatus } from '../../models/MeetingSupport'
import { ExternalUserServiceIntegration, User } from '../../models/User'

import { CustomDropdownItemOption, SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { IntegrationExportButton } from '../../components/IntegrationExportButton/IntegrationExportButton'
import { UserTagDropdownItemOption } from '../../components/SelectDropdown/UserTagDropdownItemOption'
import styles from '../../components/SelectDropdown/SelectDropdown.module.scss'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { Divider } from '../../components/Divider/Divider'
import { Button } from '../../components/Button/Button'

import { ReactComponent as CompletedTaskStatus } from '../../assets/images/task_status_completed.svg'
import { ReactComponent as SyncedTaskStatus } from '../../assets/images/task-status-synced.svg'
import { ReactComponent as DefaultTaskStatus } from '../../assets/images/task_status_default.svg'

import { getTaskSourceKey, TaskSource, useTaskMutations } from './hooks'
import { getLexicalValue, zocksUserToSelectOption } from './utils'
import { SelectionHandler, TaskDisplay } from './TaskDisplay'
import { RichTextToolbarPlugin } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { EditorState, LexicalEditor } from 'lexical'
import {
  getEditorHtmlContent,
  getEditorStateFromHtml,
  getHtmlContentFromState,
} from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'

import './Tasks.scss'

const _USER_DROPDOWN_PLACEHOLDER = 'Select person\u2026'

interface UserGroupTasksProps {
  user: User
  tasks: Task[]
  row: number
  formTaskToCreateTaskPayload: (assignedToUserId: string, formTask: NewFormTask) => CreateTaskPayload
  taskActionCallback?: (tasks: Task[]) => void
  defaultRelatedToOptions?: User[]
  initialRelatedToUser?: User | null
  selectionHandlers?: SelectionHandler[]
}

export const UserGroupTasks: React.FC<UserGroupTasksProps> = ({
  user,
  tasks,
  row,
  formTaskToCreateTaskPayload,
  taskActionCallback,
  defaultRelatedToOptions = [],
  initialRelatedToUser = null,
  selectionHandlers,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(row !== 0)

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev)
  }

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex align-items-center cursor-pointer justify-content-between' onClick={toggleCollapse}>
        <div className='d-flex gap-2 align-items-center paragraph-medium bold'>
          <UserAvatar
            width='1.5rem'
            height='1.5rem'
            fontSize='0.75rem'
            className='zocks-bg-color-gray-900 zocks-text-white'
            useInitials={true}
            initialLetters={user.name ? user.name[0] : ''}
          />
          <div>{user.name}</div>
        </div>
        <div className='py-1 px-2'>
          <span className='paragraph-medium'>
            {tasks.length}/{tasks.filter((t) => t.status === TaskStatus.CLOSED).length}
          </span>
        </div>
      </div>
      <Collapse in={!collapsed}>
        <div>
          <div className='d-flex flex-column gap-3'>
            {tasks.map((task) => (
              <UserTask key={task.id} task={task} taskActionCallback={taskActionCallback} selectionHandlers={selectionHandlers} />
            ))}
            <AddUserTasks
              user={user}
              formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
              taskActionCallback={taskActionCallback}
              defaultRelatedToOptions={defaultRelatedToOptions}
              initialRelatedToUser={initialRelatedToUser}
            />
          </div>
        </div>
      </Collapse>
    </div>
  )
}

interface AddUserGroupTasksProps {
  formTaskToCreateTaskPayload: (assignedToUserId: string, formTask: NewFormTask) => CreateTaskPayload
  taskActionCallback?: (tasks: Task[]) => Promise<void>
  defaultRelatedToOptions?: User[]
  initialRelatedToUser?: User | null
}

export const AddUserGroupTasks: React.FC<AddUserGroupTasksProps> = ({
  formTaskToCreateTaskPayload,
  taskActionCallback,
  defaultRelatedToOptions = [],
  initialRelatedToUser = null,
}) => {
  const [show, setShow] = useState<boolean>(false)

  const handleClear = () => {
    setShow(false)
  }

  if (!show) {
    return (
      <Button variant='primaryGhost' className='add-task-button' onClick={() => setShow(true)}>
        + Add User Task
      </Button>
    )
  }

  return (
    <>
      <Divider />
      <AddUserTasks
        formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
        taskActionCallback={taskActionCallback}
        initialTaskCount={1}
        onClear={handleClear}
        defaultRelatedToOptions={defaultRelatedToOptions}
        initialRelatedToUser={initialRelatedToUser}
      />
    </>
  )
}

export interface NewFormTask {
  displayId: string
  subject: string
  description: string
  relatedToUserId: string
}

interface FormValues {
  assignedToUserId: string
  tasks: NewFormTask[]
}

interface FormTaskFieldProps {
  index: number
  isLoading: boolean
  onRemove: () => void
  defaultRelatedToOptions?: User[]
  initialRelatedToUser?: User | null
  hideRelatedToField?: boolean
}

const FormTaskField: React.FC<FormTaskFieldProps> = ({
  index,
  isLoading,
  onRemove,
  defaultRelatedToOptions = [],
  initialRelatedToUser = null,
  hideRelatedToField = false,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const editorRef = useRef<LexicalEditor>(null)

  const [relatedToSearchInput, setRelatedToSearchInput] = useState<string>('')
  const [selectedRelatedToOption, setSelectedRelatedToOption] = useState<SelectOption | null>(() => {
    if (!initialRelatedToUser || hideRelatedToField) {
      return null
    }
    return zocksUserToSelectOption(initialRelatedToUser)
  })

  const { data: relatedToUsers } = useGetUsersQuery(
    {
      accountId,
      name: relatedToSearchInput,
      identityTypes: ['external'],
      pageRequest: { page: 0, size: 50 },
    },
    { skip: hideRelatedToField },
  )

  const { control, setValue } = useFormContext<FormValues>()

  const relatedToOptions = useMemo(() => {
    if (!relatedToUsers?.data) {
      return []
    }
    const defaultOptions = defaultRelatedToOptions.filter((e) => e.name).map(zocksUserToSelectOption)
    const options = relatedToUsers.data.filter((e) => e.name).map(zocksUserToSelectOption)
    return [...defaultOptions, ...options]
  }, [defaultRelatedToOptions, relatedToUsers])

  useEffect(() => {
    setValue(`tasks.${index}.relatedToUserId`, selectedRelatedToOption && !hideRelatedToField ? selectedRelatedToOption.id : '')
  }, [hideRelatedToField, index, selectedRelatedToOption, setValue])

  const handleRelatedToSearchInputChange = async (relatedToSearchInput: string) => {
    setRelatedToSearchInput(relatedToSearchInput)
  }

  const handleRelatedToOptionSelected = (option: SelectOption) => {
    setSelectedRelatedToOption(option)
  }

  return (
    <div className='d-flex gap-3 zocks-bg-color-gray-100 p-3 rounded-1' key={index}>
      <div className='d-flex flex-column gap-3 flex-grow-1'>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task name</Form.Label>
          <Controller
            control={control}
            name={`tasks.${index}.subject`}
            rules={{ required: 'Task name is required' }}
            render={({ field, fieldState }) => (
              <>
                <Form.Control value={field.value} onChange={field.onChange} placeholder='Enter your task' disabled={isLoading} />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </>
            )}
          />
        </div>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task description</Form.Label>

          <Controller
            control={control}
            name={`tasks.${index}.description`}
            render={({ field }) => {
              if (isLoading) {
                return <ReadOnlyLexicalEditor initialEditorState={getEditorStateFromHtml(field.value ?? '')} lexicalNodes={LEXICAL_NODES} />
              }
              return (
                <WritableLexicalEditor
                  ref={editorRef}
                  initialEditorState={getEditorStateFromHtml(field.value ?? '')}
                  lexicalNodes={LEXICAL_NODES}
                  header={<RichTextToolbarPlugin />}
                  onChange={(editorState: EditorState) => {
                    field.onChange(getHtmlContentFromState(JSON.stringify(editorState)))
                  }}
                  editorContainerClassName='user-task-description-editor-content'
                />
              )
            }}
          />
        </div>
        {!hideRelatedToField ? (
          <div className='d-flex flex-column'>
            <Form.Label className='lead-extra-small'>Related Client or Teammate</Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              placeholder={_USER_DROPDOWN_PLACEHOLDER}
              searchable={true}
              options={relatedToOptions}
              initialSelectedOption={selectedRelatedToOption}
              onOptionSelected={handleRelatedToOptionSelected}
              onSearchInputChange={handleRelatedToSearchInputChange}
              DropDownItemOptionComponent={UserTagDropdownItemOption}
            />
          </div>
        ) : null}
      </div>
      <div>
        <i role='button' className='bi bi-trash3 task-delete-icon' onClick={onRemove} />
      </div>
    </div>
  )
}

interface AddUserTasksProps {
  formTaskToCreateTaskPayload: (assignedToUserId: string, formTask: NewFormTask) => CreateTaskPayload
  taskActionCallback?: (tasks: Task[]) => void
  onClear?: () => void
  user?: User
  initialTaskCount?: number
  defaultRelatedToOptions?: User[]
  initialRelatedToUser?: User | null
}

export const AddUserTasks: React.FC<AddUserTasksProps> = ({
  onClear,
  formTaskToCreateTaskPayload,
  taskActionCallback,
  user = null,
  initialTaskCount = 0,
  defaultRelatedToOptions = [],
  initialRelatedToUser = null,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()

  const tasks = useAppSelector(selectTasks)
  const { createTasks, isCreatingTasks } = useTaskMutations()

  const [assignedToSearchInput, setAssignedToSearchInput] = useState<string>('')
  const [assignedToOptions, setAssignedToOptions] = useState<SelectOption[]>([])

  const { data: assignedToUsers } = useGetUsersQuery(
    {
      accountId,
      name: assignedToSearchInput,
      pageRequest: { page: 0, size: 50 },
    },
    { skip: !!user },
  )

  const defaultValues: FormValues = useMemo(
    () => ({
      assignedToUserId: user?.id ?? '',
      tasks: Array.from<NewFormTask>(
        Array(initialTaskCount).fill({
          displayId: nanoid(),
          subject: '',
          description: '',
          relatedToUserId: '',
        }),
      ),
    }),
    [initialTaskCount, user],
  )

  const form = useForm<FormValues>({ defaultValues })

  const formTasks = form.watch('tasks')
  const assignedToUserId = form.watch('assignedToUserId')

  useEffect(() => {
    if (!assignedToUsers?.data) {
      return
    }
    const options = assignedToUsers.data.filter((e) => e.name).map(zocksUserToSelectOption)
    setAssignedToOptions(options)
  }, [assignedToUsers])

  const handleCreateCallback = useCallback(
    (createdTasks: Task[]) => {
      if (!taskActionCallback) {
        return
      }
      const _tasks = [...tasks, ...createdTasks]
      taskActionCallback(_tasks)
    },
    [taskActionCallback, tasks],
  )

  const _handleSubmit = useCallback(
    async (data: FormValues) => {
      const { assignedToUserId, tasks: formTasks } = data
      if (!formTasks.length) {
        return
      }
      try {
        const payload = formTasks.map((formTask) => formTaskToCreateTaskPayload(assignedToUserId, formTask))
        const createdTasks = await createTasks(payload)
        if (createdTasks) {
          handleCreateCallback(createdTasks)
        }
      } catch (e) {
        console.error(e)
      } finally {
        form.reset()
        if (onClear) {
          onClear()
        }
      }
    },
    [createTasks, handleCreateCallback, formTaskToCreateTaskPayload, form, onClear],
  )

  const handleSaveTask = tracked(_handleSubmit, {
    eventName: TrackEvent.TASK_CREATED,
  })

  const handleAddTask = useCallback(() => {
    const _formTasks = [...formTasks, { displayId: nanoid(), subject: '', description: '', relatedToUserId: '' }]
    form.setValue('tasks', _formTasks)
  }, [form, formTasks])

  const handleCancel = useCallback(() => {
    form.reset()
    if (onClear) {
      onClear()
    }
  }, [form, onClear])

  const onAssignedToSearchInputChange = async (assignedToSearchInput: string) => {
    setAssignedToSearchInput(assignedToSearchInput)
  }

  return (
    <FormProvider {...form}>
      {!user ? (
        <div className='d-flex gap-3 align-items-center'>
          <UserAvatar width='1.5rem' height='1.5rem' fontSize='0.75rem' className='zocks-bg-color-gray-900 zocks-text-white flex-shrink-0' />
          <Controller
            control={form.control}
            name='assignedToUserId'
            render={({ field }) => {
              const handleOptionSelected = (option: SelectOption) => {
                field.onChange(option.id)
              }

              return (
                <SelectDropdown
                  className='py-2 zocks-px-0.75'
                  placeholder={_USER_DROPDOWN_PLACEHOLDER}
                  searchable={true}
                  options={assignedToOptions}
                  initialSelectedOption={null}
                  onOptionSelected={handleOptionSelected}
                  onSearchInputChange={onAssignedToSearchInputChange}
                  DropDownItemOptionComponent={UserTagDropdownItemOption}
                />
              )
            }}
          />
        </div>
      ) : null}
      {formTasks.length ? (
        <div className='d-flex flex-column gap-3'>
          {formTasks.map((formTask, index) => {
            const handleRemove = () => {
              const _formTasks = formTasks.filter((_, i) => i !== index)
              if (!_formTasks.length) {
                handleCancel()
              }
              form.setValue('tasks', _formTasks)
            }

            const assignedToUser = user ?? assignedToUsers?.data.find((e) => e.id === assignedToUserId)

            return (
              <FormTaskField
                key={formTask.displayId}
                index={index}
                isLoading={isCreatingTasks}
                onRemove={handleRemove}
                defaultRelatedToOptions={defaultRelatedToOptions.filter((e) => e.id !== assignedToUser?.id)}
                initialRelatedToUser={initialRelatedToUser}
                hideRelatedToField={assignedToUser ? assignedToUser.type === 'external' : false}
              />
            )
          })}
        </div>
      ) : null}
      <Button
        className='text-decoration-none justify-content-start zocks-text-primary button-small mt-2'
        variant='link'
        onClick={handleAddTask}
        disabled={isCreatingTasks}
      >
        + Add task
      </Button>
      {formTasks.length ? (
        <div className='d-flex gap-3 justify-content-end'>
          <Button className='button-small' variant='secondary' onClick={handleCancel}>
            Cancel
          </Button>
          <Button className='button-small' variant='primary' onClick={form.handleSubmit(handleSaveTask)} loading={isCreatingTasks} disabled={!assignedToUserId}>
            Save
          </Button>
        </div>
      ) : null}
    </FormProvider>
  )
}

interface UserTaskProps {
  task: Task
  taskActionCallback?: (tasks: Task[]) => void
  selectionHandlers?: SelectionHandler[]
}

const UserTask: React.FC<UserTaskProps> = ({ task, taskActionCallback, selectionHandlers }) => {
  const dispatch = useAppDispatch()
  const editorRef = useRef<LexicalEditor>(null)

  const tasks = useAppSelector(selectTasks)

  const [taskSubject, setTaskSubject] = useState<string>(task.task)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [showIcons, setShowIcons] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  const { updateTaskById, isUpdatingTask, deleteTask, isDeletingTask } = useTaskMutations()
  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  useOutsideClick(dropdownRef, () => {
    setIsDropdownOpen(false)
  })

  const lexicalValue = useMemo(() => getLexicalValue(task), [task])

  const handleUpdateCallback = useCallback(
    (updatedTask: Task) => {
      if (!taskActionCallback) {
        return
      }
      const _tasks = tasks.map((e) => (e.id === task.id ? updatedTask : e))
      taskActionCallback(_tasks)
    },
    [task.id, taskActionCallback, tasks],
  )

  const handleDeleteCallback = useCallback(() => {
    if (!taskActionCallback) {
      return
    }
    const _tasks = tasks.filter((e) => e.id !== task.id)
    taskActionCallback(_tasks)
  }, [task.id, taskActionCallback, tasks])

  const handleTaskChange = useCallback(async () => {
    try {
      const taskDescription = editorRef.current ? getEditorHtmlContent(editorRef.current.getEditorState()) : null
      const payload = { task: taskSubject, description: taskDescription ?? undefined }
      const updatedTask = await updateTaskById(task.id, payload)
      if (updatedTask) {
        handleUpdateCallback(updatedTask)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsEditing(false)
    }
  }, [handleUpdateCallback, task.id, taskSubject, updateTaskById])

  const handleStatusChange = useCallback(async () => {
    try {
      const payload = { status: task.status === TaskStatus.OPEN ? TaskStatus.CLOSED : TaskStatus.OPEN }
      const updatedTask = await updateTaskById(task.id, payload)
      if (updatedTask) {
        handleUpdateCallback(updatedTask)
      }
    } catch (e) {
      console.error(e)
    }
  }, [handleUpdateCallback, task.id, task.status, updateTaskById])

  const handleDelete = useCallback(async () => {
    try {
      await deleteTask(task)
      handleDeleteCallback()
    } catch (e) {
      console.error(e)
    }
  }, [deleteTask, handleDeleteCallback, task])

  const handleCopy = useCallback(async () => {
    const { task: subject, description } = task

    const descriptionPart = description ? `: ${description}` : ''
    const textContent = subject + descriptionPart
    const htmlContent = `<p><strong>${subject}</strong>${descriptionPart}</p>`

    const _copyContent = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }, [copyComplianceMessage, dispatch, task])

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskSubject(e.target.value)
  }

  const handleCancel = () => {
    setTaskSubject(task.task)
    setIsEditing(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  if (isEditing) {
    return (
      <div className='d-flex flex-column gap-3 zocks-bg-color-gray-100 p-3 rounded-1'>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task name</Form.Label>
          <Form.Control value={taskSubject} onChange={handleSubjectChange} />
        </div>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task description</Form.Label>
          <WritableLexicalEditor
            ref={editorRef}
            initialEditorState={getEditorStateFromHtml(task.description ?? '')}
            lexicalNodes={LEXICAL_NODES}
            header={<RichTextToolbarPlugin />}
            editorContainerClassName='user-task-description-editor-content'
          />
        </div>
        <div className='d-flex gap-3 justify-content-end'>
          <Button className='button-small' variant='secondary' onClick={handleCancel}>
            Cancel
          </Button>
          <Button className='button-small' variant='primary' onClick={handleTaskChange} loading={isUpdatingTask}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  const taskOptions = [
    {
      selectOption: {
        id: 'copy',
        label: 'Copy',
      },
      icon: 'bi-copy',
      handleSelected: handleCopy,
      disabled: accountIsFetching,
    },
    {
      selectOption: {
        id: 'delete',
        label: 'Delete',
      },
      icon: 'bi bi-trash3',
      className: 'zocks-text-danger',
      handleSelected: handleDelete,
    },
  ]

  return (
    <div
      className={classNames('flex-grow-1 d-flex gap-2', { 'zocks-text-gray-500 text-decoration-line-through': task.status === TaskStatus.CLOSED })}
      onMouseEnter={() => setShowIcons(true)}
      onMouseLeave={() => setShowIcons(false)}
    >
      <TaskStatusComponent task={task} onClick={handleStatusChange} isLoading={isUpdatingTask || isDeletingTask} />
      <TaskDisplay key={lexicalValue} lexicalValue={lexicalValue} selectionHandlers={selectionHandlers} />
      <div className={classNames('d-flex gap-3', { invisible: !showIcons && !isDropdownOpen })}>
        {!task.syncStatus.length ? <i role='button' className='bi bi-pencil-square paragraph-large zocks-text-gray-900' onClick={handleEdit} /> : null}
        <TaskOptionsDropdown ref={dropdownRef} options={taskOptions} isOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen} />
      </div>
    </div>
  )
}

export interface TaskStatusComponentProps {
  task: Task
  onClick: () => void
  isLoading?: boolean
}

export const TaskStatusComponent: React.FC<TaskStatusComponentProps> = ({ task, onClick, isLoading = false }) => {
  const StatusComponent =
    task.syncStatus.length > 0 ? (
      <SyncedTaskStatus />
    ) : isLoading ? (
      <Spinner size={'sm'} />
    ) : task.status === TaskStatus.CLOSED ? (
      <CompletedTaskStatus />
    ) : (
      <DefaultTaskStatus />
    )

  return (
    <Button className='p-0 task-status-button flex-shrink-0' onClick={onClick} disabled={isLoading || !!task.syncStatus.length}>
      {StatusComponent}
    </Button>
  )
}

const getTaskSourceLabel = (taskSource: TaskSource) => {
  const { type, source } = taskSource
  if (type === 'meeting') {
    const { external } = parseMetadata(source.metadata)
    return external.title
  }
  return source.subject
}

const getTaskSourceLink = (taskSource: TaskSource) => {
  const { type, source } = taskSource
  if (type === 'meeting') {
    return `/workspace/past-meetings/room/${source.roomId}/session/${source.id}`
  }
  return `/email/inbox/threads/${source.threadId}`
}

interface TaskSourceComponentProps {
  task: Task
  taskSources: TaskSource[]
  isHovered?: boolean
}

export const TaskSourceComponent: React.FC<TaskSourceComponentProps> = ({ task, taskSources, isHovered = false }) => {
  const taskSource = useMemo(() => {
    const taskSourceKey = getTaskSourceKey(task)
    const taskSource = taskSources.find((e) => e.sourceKey === taskSourceKey)
    if (!taskSource) {
      return null
    }
    return taskSource
  }, [task, taskSources])

  const sourceLabel = useMemo(() => (taskSource ? getTaskSourceLabel(taskSource) : null), [taskSource])

  const sourceLink = useMemo(() => (taskSource ? getTaskSourceLink(taskSource) : null), [taskSource])

  if (!sourceLabel) {
    return null
  }

  const sourceIconClassName = task.type === 'meeting' ? 'bi-calendar' : 'bi-envelope'

  return (
    <div className='d-flex align-items-center gap-2 paragraph-small'>
      <i className={`bi ${sourceIconClassName}`} />
      {sourceLink ? (
        <Link
          className={classNames('zocks-text-gray-900', { 'text-decoration-underline': isHovered })}
          to={sourceLink}
          target='_blank'
          rel='noopener noreferrer'
        >
          {sourceLabel}
        </Link>
      ) : (
        <span>{sourceLabel}</span>
      )}
    </div>
  )
}

export interface TaskOptionsDropdownProps {
  options: ResultOption[]
  isOpen: boolean
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const TaskOptionsDropdown = React.forwardRef<HTMLDivElement, TaskOptionsDropdownProps>(({ options, setIsDropdownOpen, isOpen }, ref) => {
  const [alignDropdownMenuRight, setAlignDropdownMenuRight] = useState<boolean>(false)
  const dropdownMenuRef = useRef<HTMLDivElement>(null)

  const dropdownMenuCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      ;(dropdownMenuRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      if (!isOpen || !node) {
        setAlignDropdownMenuRight(false)
        return
      }
      const rect = node.getBoundingClientRect()
      setAlignDropdownMenuRight(rect.right > window.innerWidth)
    },
    [isOpen],
  )

  const DropDownItemOption: React.FC<{ option: ResultOption }> = ({ option }) => {
    const { selectOption, icon, className, handleSelected } = option

    const handleClick = () => {
      handleSelected(selectOption)
      setIsDropdownOpen(false)
    }

    return (
      <CustomDropdownItemOption role='button' onClick={handleClick} className={`d-flex gap-2 align-items-center ${className}`}>
        <i className={`bi ${icon}`} />
        {selectOption.label}
      </CustomDropdownItemOption>
    )
  }

  return (
    <div ref={ref} className='p-0 position-relative task-options-dropdown'>
      <i role='button' className='bi bi-three-dots-vertical paragraph-large zocks-text-gray-900' onClick={() => setIsDropdownOpen((prev) => !prev)} />
      <div
        ref={dropdownMenuCallbackRef}
        className={classNames(`dropdown-menu paragraph-small ${styles.selectDropdownMenu}`, {
          show: isOpen,
          'end-0': alignDropdownMenuRight,
        })}
        aria-labelledby='selectDropdown'
      >
        <div className={`${styles.selectDropdownOptionsContainer}`}>
          {options.map((option) => (
            <DropDownItemOption key={option.selectOption.id} option={option} />
          ))}
        </div>
      </div>
    </div>
  )
})

interface UserTasksHeaderProps {
  onShowCRMModal: (integration: ExternalUserServiceIntegration) => void
}

export const UserTasksHeader: React.FC<UserTasksHeaderProps> = ({ onShowCRMModal }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: currentUser } = useGetMeQuery({ accountId })

  const taskExportSupportedUserIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => TASK_EXPORT_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser],
  )

  return (
    <Row className='mt-3'>
      <Col xs={'auto'} style={{ width: '2rem' }} />
      <Col className='d-flex align-items-center' style={{ flex: 2 }}>
        <span className='lead-small'>Task & source</span>
      </Col>
      <Col className='d-flex align-items-center justify-content-between' style={{ flex: 1 }}>
        <span className='lead-small'>Assignee</span>
        <div className='d-flex align-items-center gap-2'>
          {taskExportSupportedUserIntegrations.map((integration) => (
            <IntegrationExportButton key={integration.serviceId} serviceId={integration.serviceId} onClick={() => onShowCRMModal(integration)} />
          ))}
        </div>
      </Col>
    </Row>
  )
}

interface UserTaskRowProps {
  task: Task
  taskSources: TaskSource[]
}

export const UserTaskRow: React.FC<UserTaskRowProps> = ({ task, taskSources }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const { updateTaskById, isUpdatingTask, deleteTask, isDeletingTask } = useTaskMutations()

  const [taskSubject, setTaskSubject] = useState<string>(task.task)
  const [taskDescription, setTaskDescription] = useState<string | undefined>(task.description)

  const [isEditing, setIsEditing] = useState<boolean>(false)

  const [showIcons, setShowIcons] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  useOutsideClick(dropdownRef, () => {
    setIsDropdownOpen(false)
  })

  const lexicalValue = useMemo(() => getLexicalValue(task), [task])

  const handleTaskChange = useCallback(async () => {
    try {
      const payload = { task: taskSubject, description: taskDescription }
      await updateTaskById(task.id, payload)
    } catch (e) {
      console.error(e)
    } finally {
      setIsEditing(false)
    }
  }, [task.id, taskDescription, taskSubject, updateTaskById])

  const handleStatusChange = useCallback(async () => {
    try {
      const payload = { status: task.status === TaskStatus.OPEN ? TaskStatus.CLOSED : TaskStatus.OPEN }
      await updateTaskById(task.id, payload)
    } catch (e) {
      console.error(e)
    }
  }, [task.id, task.status, updateTaskById])

  const handleDelete = useCallback(async () => {
    try {
      await deleteTask(task)
    } catch (e) {
      console.log(e)
    }
  }, [deleteTask, task])

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskSubject(e.target.value)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskDescription(e.target.value)
  }

  const handleCancel = () => {
    setTaskSubject(task.task)
    setTaskDescription(task.description)
    setIsEditing(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const assignedToUser = task.assignedTo?.user

  if (!assignedToUser) {
    return null
  }

  const options = [
    {
      selectOption: {
        id: 'delete',
        label: 'Delete',
      },
      className: 'zocks-text-danger',
      icon: 'bi bi-trash3',
      handleSelected: handleDelete,
    },
  ]

  if (isEditing) {
    return (
      <div className='d-flex flex-column gap-3 zocks-bg-color-gray-100 p-3 rounded-1'>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task name</Form.Label>
          <Form.Control value={taskSubject} onChange={handleSubjectChange} />
        </div>
        <div className='d-flex flex-column'>
          <Form.Label className='lead-extra-small'>Task description</Form.Label>
          <Form.Control value={taskDescription} onChange={handleDescriptionChange} />
        </div>
        <div className='d-flex gap-3 justify-content-end'>
          <Button className='button-small' variant='secondary' onClick={handleCancel}>
            Cancel
          </Button>
          <Button className='button-small' variant='primary' onClick={handleTaskChange} loading={isUpdatingTask}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Row onMouseEnter={() => setShowIcons(true)} onMouseLeave={() => setShowIcons(false)}>
      <Col xs={'auto'} style={{ width: '2rem' }} className='d-flex justify-content-center'>
        <TaskStatusComponent task={task} onClick={handleStatusChange} isLoading={isUpdatingTask || isDeletingTask} />
      </Col>
      <Col className='d-flex flex-column gap-2' style={{ flex: 2 }}>
        <div
          className={classNames('paragraph-medium bold', {
            'zocks-text-gray-500 text-decoration-line-through': task.status === TaskStatus.CLOSED || task.syncStatus.length,
          })}
        >
          <ReadOnlyLexicalEditor key={lexicalValue} initialEditorState={lexicalValue} lexicalNodes={LEXICAL_NODES} />
        </div>
        <TaskSourceComponent task={task} taskSources={taskSources} isHovered={showIcons} />
      </Col>
      <Col className='d-flex gap-2 align-items-center justify-content-between paragraph-medium' style={{ flex: 1 }}>
        <div className='d-flex gap-3 align-items-center flex-wrap'>
          <UserAvatar
            width='1.5rem'
            height='1.5rem'
            fontSize='0.75rem'
            className='zocks-bg-color-gray-900 zocks-text-white bold'
            useInitials={true}
            initialLetters={assignedToUser.name ? assignedToUser.name[0] : ''}
          />
          <span>{assignedToUser.name}</span>
          <UserTag userType={assignedToUser.id === currentUser.id ? 'current' : assignedToUser.type} />
        </div>
        <div className={classNames('d-flex gap-3 align-items-center', { invisible: !showIcons && !isDropdownOpen })}>
          {!task.syncStatus.length && <i role='button' className='bi bi-pencil-square paragraph-medium zocks-text-gray-900' onClick={handleEdit} />}
          <TaskOptionsDropdown ref={dropdownRef} options={options} isOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen} />
        </div>
      </Col>
    </Row>
  )
}
